import { inject, Injectable } from '@angular/core';
import { HttpCoreService } from './httpCoreService.service';
import { Endpoints } from '../constants/endpoints';

interface FlatSystemOption {
  route: string;
  name: string;
}

/**
 * `router.url`/`state.url` vienen serializados por Angular con los segmentos
 * percent-encoded (p.ej. "añadir-grupo" → "a%C3%B1adir-grupo"), a diferencia de las
 * rutas de system_option, que se guardan en BD sin encodear. Sin decodificar antes de
 * comparar, cualquier ruta con tildes/ñ/espacios nunca hace match y queda bloqueada.
 */
export function safeDecodeUriPath(path: string): string {
  try {
    return decodeURIComponent(path);
  } catch {
    return path;
  }
}

export interface ResolvedRouteOption {
  name: string;
  isExact: boolean;
}

// Rutas de Angular que no anidan literalmente bajo el `route` de su opción padre en
// system_option (son "hermanas" en el path, no hijas), así que el match por prefijo
// de segmentos no las resuelve solas. Mapeadas a mano a su(s) padre(s) lógico(s). La
// clave se matchea como prefijo de segmentos igual que el árbol real, así que una
// sola entrada cubre también sus propias sub-rutas (p.ej. 'precio-suscripcion' cubre
// 'precio-suscripcion/crear' y 'precio-suscripcion/actualizar'). Una ruta puede tener
// más de un padre posible cuando la misma pantalla se llega desde dos secciones
// distintas del árbol según el tipo de usuario (se usa el primero que esté habilitado
// para el usuario actual).
const MANUAL_PARENT_OVERRIDES: Record<string, string[]> = {
  'gestion-pagos-empresa-periodo': ['/gestion-pagos'],
  'detalle-facturacion': ['/facturacion'],
  'notificaciones/registrar-template': ['/notificaciones/templates'],
  'notificaciones/actualizar-template': ['/notificaciones/templates'],
  // Se accede desde dentro de "Empresas" (no es un ítem de menú propio): hereda
  // el permiso de /empresas y nunca debe tener su propia fila en system_option.
  'precio-suscripcion': ['/empresas'],
  // BandejaCollaboratorsComponent (y sus acciones) se llega desde /usuarios/colaboradores
  // (Admin iPay) o desde /colaboradores/editar (Business Admin) según el tipo de usuario.
  // Se alían acá también en sentido inverso: varios flujos (crear colaborador, inactivar
  // colaboradores) navegan de vuelta a la URL hardcodeada /usuarios/colaboradores al
  // guardar/cancelar, sin importar por cuál de las dos entró el usuario. Como ambas URLs
  // renderizan el mismo componente con los mismos datos, tratarlas como alias no expone
  // nada nuevo — solo evita que Business Admin quede bloqueado al volver a la lista.
  'usuarios/colaboradores': ['/usuarios/colaboradores', '/colaboradores/editar'],
  'usuarios/colaboradores/crear': ['/usuarios/colaboradores', '/colaboradores/editar'],
  'usuarios/colaboradores/añadir-grupo': ['/usuarios/colaboradores', '/colaboradores/editar'],
  'usuarios/colaboradores/inactivar-grupo': ['/usuarios/colaboradores', '/colaboradores/editar'],
  'usuarios/colaboradores/inactivar-colaboradores': ['/usuarios/colaboradores', '/colaboradores/editar'],
  'usuarios/colaboradores/suspender-grupo': ['/usuarios/colaboradores', '/colaboradores/editar'],
};

/**
 * Resuelve rutas de /dashboard/company/* contra el árbol de opciones habilitado
 * para el usuario actual (`user__get_menu`, ya filtrado por tipo de usuario en BD).
 * Sirve tanto al guard de permisos como al título de la pestaña: ambos necesitan
 * saber a qué system_option pertenece (o hereda) la ruta activa.
 */
@Injectable({
  providedIn: 'root'
})
export class RouteMetadataService {
  private readonly httpCoreService = inject(HttpCoreService);

  private cache: FlatSystemOption[] | null = null;
  private cachePromise: Promise<FlatSystemOption[]> | null = null;

  /** Se debe llamar en todo login/logout: el árbol depende del usuario autenticado. */
  clearCache(): void {
    this.cache = null;
    this.cachePromise = null;
  }

  async isRouteAllowed(pathAfterCompany: string): Promise<boolean> {
    return (await this.resolve(pathAfterCompany)) !== null;
  }

  /**
   * `pathAfterCompany` es el path relativo a /dashboard/company (p.ej. "empresas/crear").
   * Si no tiene su propio system_option (página derivada de un formulario/detalle),
   * devuelve el ancestro habilitado más cercano por segmentos de ruta, con isExact=false.
   */
  async resolve(pathAfterCompany: string): Promise<ResolvedRouteOption | null> {
    const cleanPath = safeDecodeUriPath(pathAfterCompany).replace(/^\/+|\/+$/g, '');
    if (!cleanPath) return null;

    const options = await this.getFlatOptions();
    const segments = cleanPath.split('/').filter(Boolean);

    // 1. Overrides manuales, probados también por prefijo de segmentos (de más
    //    específico a más general) para cubrir sub-rutas del path "hermano".
    for (let len = segments.length; len >= 1; len--) {
      const candidate = segments.slice(0, len).join('/');
      const overrideTargets = MANUAL_PARENT_OVERRIDES[candidate];
      if (overrideTargets) {
        for (const target of overrideTargets) {
          const match = options.find(o => o.route === target);
          if (match) return { name: match.name, isExact: false };
        }
        // Hay override definido para este path pero ninguno de sus posibles
        // padres está habilitado para el usuario actual: no hay ancestro más
        // general que probar, así que no está permitido.
        return null;
      }
    }

    // 2. Match directo (o por ancestro) contra el route real de system_option.
    for (let len = segments.length; len >= 1; len--) {
      const candidate = '/' + segments.slice(0, len).join('/');
      const match = options.find(o => o.route === candidate);
      if (match) {
        return { name: match.name, isExact: len === segments.length };
      }
    }

    return null;
  }

  private async getFlatOptions(): Promise<FlatSystemOption[]> {
    if (this.cache) return this.cache;
    if (!this.cachePromise) {
      this.cachePromise = this.fetchAndFlatten();
    }

    const options = await this.cachePromise;
    if (options.length > 0) {
      this.cache = options;
    } else {
      // No cacheamos un árbol vacío/fallido: reintentamos en la próxima navegación
      // en vez de dejar al usuario bloqueado por el resto de la sesión.
      this.cachePromise = null;
    }

    return options;
  }

  private async fetchAndFlatten(): Promise<FlatSystemOption[]> {
    try {
      const tree = await this.httpCoreService.executeRequest(Endpoints.UserGetMenu);
      const flat: FlatSystemOption[] = [];

      const walk = (nodes: any[]) => {
        for (const node of nodes || []) {
          if (node?.route) {
            flat.push({ route: this.normalize(node.route), name: node.name });
          }
          if (node?.children?.length) {
            walk(node.children);
          }
        }
      };

      walk(Array.isArray(tree) ? tree : []);
      return flat;
    } catch (error) {
      console.error('[RouteMetadataService] Error obteniendo árbol de opciones:', error);
      return [];
    }
  }

  private normalize(route: string): string {
    return route.startsWith('/') ? route : `/${route}`;
  }
}
